import axios from "axios";
import { DOMAIN_NAME } from "config";
import { errorStatusCodes } from "constants/common";
import { setErrorAndShowExitBtn } from "store/features/clientSlice";

import { RootState, store } from "store/store";
import { LSKeys, getFromLocalStorage } from "utils/ls.utils";
import {
  API_VERSION,
  failEmbedSession,
  refreshHostedEmbedSession,
  sendMessageToClient,
} from "utils/utils";
import {
  getEmbedAuthErrorCode,
  getEmbedFailureReason,
  getHostedInsuranceAuthHeaders,
  isEmbedSessionTokenExpiring,
} from "utils/hostedAuth";

export const callApi = async <Response>(
  apiConfig: CallAPIConfig,
  includeVersionInBaseURL: boolean = true,
  state?: RootState
): Promise<Response> => {
  let reduxState = { ...state };
  if (!state) {
    const reduxStore = await import("store/store"); //dynamic import to avoid cyclic dependency
    reduxState = reduxStore.store.getState();
  }
  const initialConfigObject: Partial<CallAPIConfig> = {
    method: "GET",
    authenticated: true,
    params: {},
    body: {},
    headers: {},
    clientId: reduxState?.client?.clientAPIKey,
  };

  const { endpoint, method, headers, params, body, clientId } = {
    ...initialConfigObject,
    ...apiConfig,
  };
  const accessToken = getFromLocalStorage(LSKeys.ACCESS_TOKEN);
  const domain = getFromLocalStorage(LSKeys.DOMAIN);
  if (reduxState?.client?.hostedAuthMode === "embed_session") {
    if (
      !reduxState.client.embedAccessToken ||
      !reduxState.client.embedAccessTokenExpiresAt
    ) {
      failEmbedSession("secure_session_failed");
      throw new Error("Hosted embed session is not active.");
    }
    if (isEmbedSessionTokenExpiring(reduxState.client.embedAccessTokenExpiresAt)) {
      const refreshed = await refreshHostedEmbedSession();
      if (!refreshed) {
        throw new Error("Hosted embed session refresh failed.");
      }
      reduxState = store.getState();
    }
  }
  const hostedAuthHeaders = getHostedInsuranceAuthHeaders(reduxState);
  const mandatoryHeaders = {
    "Content-Type": "application/json",
    ...(hostedAuthHeaders.Authorization
      ? { Authorization: hostedAuthHeaders.Authorization }
      : { "X-API-KEY": hostedAuthHeaders["X-API-KEY"] ?? clientId }),
    ...(accessToken
      ? { "X-STERE-USER-TOKEN": accessToken, "X-STERE-DOMAIN": domain ?? DOMAIN_NAME }
      : {}),
    // Authorization: process.env.REACT_APP_SIA_AUTH_TOKEN,
  };

  const requestHeaders = {
    ...mandatoryHeaders,
    ...headers,
  };

  const config = {
    method,
    params,
    headers: requestHeaders,
    data: body,
    url: `${
      process.env.REACT_APP_SERVER_URL +
      (includeVersionInBaseURL ? `/${API_VERSION}` : "") +
      endpoint
    }`,
  };

  return axios
    .request(config)
    .then((response) => response.data)
    .catch(async (error) => {
      if (axios.isCancel(error)) {
        throw { ...error, isCanceled: true };
      } else if (reduxState?.client?.hostedAuthMode === "embed_session") {
        const code = getEmbedAuthErrorCode(error);
        if (
          error?.response?.status === 401 &&
          code === "EMBED_TOKEN_EXPIRED" &&
          !(config as any)._embedRetry
        ) {
          (config as any)._embedRetry = true;
          const refreshed = await refreshHostedEmbedSession();
          if (refreshed) {
            const retryHeaders = {
              ...requestHeaders,
              ...getHostedInsuranceAuthHeaders(store.getState()),
            };
            return axios
              .request({
                ...config,
                headers: retryHeaders,
              })
              .then((response) => response.data);
          }
        }
        if (code === "EMBED_SESSION_EXPIRED") {
          failEmbedSession("embed_session_expired", true);
        } else if (code?.startsWith("EMBED_")) {
          failEmbedSession(getEmbedFailureReason(code));
        } else if (
          error.response &&
          errorStatusCodes.includes(error.response.status)
        ) {
          failEmbedSession("secure_session_failed");
        }
      } else if (
        error.response &&
        errorStatusCodes.includes(error.response.status)
      ) {
        store?.dispatch(setErrorAndShowExitBtn(true));
        sendMessageToClient(
          {
            type: "FAILURE",
            details: {
              section:
              store?.getState()?.stepper?.currentStepLabel,
            },
          }
        );
      }
      throw error;
    });
};
